import * as React from 'react';
import useAutocomplete, {
  AutocompleteGetItemProps,
} from '@mui/material/useAutocomplete';
import { styled } from '@mui/material/styles';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import { Check, Plus, X } from 'lucide-react';
import debounceFn from '../../../utils/helperFunction/debounceFn';
import { AutoCompleteOptionsType } from './AutoCompleteOptionsType';

/* ----------  STYLED PARTS  ---------- */
const Root = styled('div')(({ theme }) => ({
  color: 'rgba(0,0,0,0.85)',
  fontSize: 14,
  ...theme.applyStyles('dark', { color: 'rgba(255,255,255,0.65)' }),
}));

const Label = styled('label')`
  padding: 0 0 4px;
  line-height: 1.5;
  display: block;
`;

const InputWrapper = styled('div')(() => ({
  '&.focused': {
    boxShadow: '0 0 0 2px rgb(24 144 255 / 0.2)',
  },
  '& input': {
    backgroundColor: 'transparent',
    flexGrow: 1,
    border: 0,
    outline: 0,
  },
}));

const Listbox = styled('ul')(() => ({
  padding: 0,
  '& li': {
    display: 'flex',
    padding: '8px 16px',
    '& span': { flexGrow: 1 },
    '&>svg': { color: 'transparent' },
  },
  "& li[aria-selected='true']": {
    backgroundColor: '#fafafa',
    fontWeight: 500,
    '& svg': { color: '#1890ff' },
  },
  [`& li.${autocompleteClasses.focused}`]: {
    backgroundColor: '#e6f7ff',
    cursor: 'pointer',
  },
}));

interface ItemProps extends ReturnType<AutocompleteGetItemProps<true>> {
  label: string;
}

const TagItem = styled((props: ItemProps) => {
  const { label, onDelete, ...other } = props;
  return (
    <div {...other}>
      <span>{label}</span>
      <X onClick={onDelete} />
    </div>
  );
})(() => ({
  display: 'flex',
  alignItems: 'center',
  height: 24,
  margin: 2,
  lineHeight: '22px',
  backgroundColor: '#fafafa',
  border: '1px solid #e8e8e8',
  borderRadius: 2,
  padding: '0 4px 0 10px',
  '& svg': { fontSize: 12, cursor: 'pointer', padding: 4 },
}));

/* ----------  MAIN COMPONENT  ---------- */
export interface AsyncMultiAutocompleteProps {
  /** Fetch options based on query */
  fetchOptions: (query: string) => Promise<AutoCompleteOptionsType[]>;
  /** Extract label text from each option */
  getOptionLabel: (option: AutoCompleteOptionsType) => string;
  /** Optional controlled value (array of IDs) */
  value?: Array<AutoCompleteOptionsType>;
  /** Called whenever selected IDs change */
  onChange?: (value: Array<AutoCompleteOptionsType['id']>) => void;
  /** UI */
  label?: string;
  placeholder?: string;
  debounceMs?: number;
  /** Add new option on the fly */
  addFn?: (query: string) => Promise<AutoCompleteOptionsType>;
  hintMsg?: string;
}

export function AsyncMultiAutoComplete({
  fetchOptions,
  getOptionLabel,
  value: valueProp,
  onChange,
  label = 'Select',
  placeholder = 'Type to search…',
  debounceMs = 300,
  addFn,
  hintMsg,
}: AsyncMultiAutocompleteProps) {
  const [options, setOptions] = React.useState<Array<AutoCompleteOptionsType>>(
    []
  );
  const [loading, setLoading] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const [selectedOptions, setSelectedOptions] = React.useState<
    Array<AutoCompleteOptionsType>
  >(valueProp || []);
  /* Debounced backend search */

  React.useEffect(
    debounceFn(() => {
      if (!fetchOptions) return;
      try {
        if (inputValue.trim()) {
          setLoading(true);
          fetchOptions(inputValue)
            .then((data) => setOptions(data))
            .finally(() => setLoading(false));
        } else {
          setOptions([]);
        }
      } catch (error: any) {
        console.error(error.message);
        setOptions([]);
      }
    }, debounceMs),
    [inputValue, fetchOptions, debounceMs]
  );

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getItemProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    // value,
    // focused,
    setAnchorEl,
  } = useAutocomplete<AutoCompleteOptionsType, true>({
    multiple: true,
    options: options || [],
    value: selectedOptions,
    onChange: (_, val) => {
      setSelectedOptions(val);
      onChange?.(val);
    },
    getOptionLabel,
    inputValue,
    onInputChange: (_, val) => setInputValue(val),
  });

  /* Add button logic */
  const onAddQuery = async () => {
    if (!addFn || !inputValue.trim()) return;
    try {
      setLoading(true);
      let newEntry = await addFn(inputValue.trim());
      newEntry = {
        id: newEntry?.id,
        name: newEntry?.name,
        disabled: newEntry?.disabled,
      };
      const updatedValue = [...(valueProp || []), newEntry];
      setSelectedOptions((o) => [...o, newEntry]);
      onChange?.(updatedValue);
      setInputValue('');
    } catch (err) {
      console.error('Failed to add option', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Root>
        <div className="relative">
          <div {...getRootProps()}>
            <Label {...getInputLabelProps()}>{label}</Label>
            <InputWrapper
              ref={setAnchorEl}
              className={`border border-border bg-transparent rounded-md px-3 py-2 flex flex-wrap
                          [&input]:bg-transparent [&input]:py-3 [&input]:px-4
                          [&input]:high-emphasis-text w-full`}
            >
              {selectedOptions.map((option, index) => {
                const { key, ...itemProps } = getItemProps({ index });
                return (
                  <TagItem
                    key={key}
                    {...itemProps}
                    label={getOptionLabel(option)}
                  />
                );
              })}
              <input
                {...getInputProps()}
                placeholder={loading ? 'Loading…' : placeholder}
              />
            </InputWrapper>
          </div>

          {inputValue && (
            <Listbox
              {...getListboxProps()}
              className="max-w-full mt-1 p-0 z-10 absolute top-full w-full overflow-auto bg-white list-none rounded-md max-h-48 shadow-md"
            >
              {groupedOptions.length > 0 ? (
                groupedOptions.map((option, index) => {
                  const { key, ...optionProps } = getOptionProps({
                    option,
                    index,
                  });
                  if (option.disabled || valueProp?.includes(option.id))
                    return null;
                  return (
                    <li className="capitalize" key={key} {...optionProps}>
                      <span>{getOptionLabel(option)}</span>
                      <Check className="size-4" />
                    </li>
                  );
                })
              ) : (
                <>
                  {loading && (
                    <li className="cursor-default w-full flex items-center gap-2 px-3 py-2 low-emphasis-text italic text-xs">
                      Loading...
                    </li>
                  )}
                  <li
                    key="add-query"
                    onClick={onAddQuery}
                    className="cursor-pointer w-full flex items-center gap-2 px-3 py-2"
                  >
                    <div>
                      <Plus className="size-4 high-emphasis-text" />
                    </div>
                    <span className="flex-1">Add “{inputValue}”</span>
                  </li>
                </>
              )}
            </Listbox>
          )}
        </div>
      </Root>
      {hintMsg && (
        <label className="text-xs text-red-600 mt-1">{hintMsg}</label>
      )}
    </div>
  );
}
